import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AutoStatusToast from '../common/AutoStatusToast';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';
import {
  deleteBetaEventCodeRequest,
  listBetaEventCodeRequests,
  markBetaEventCodeRequestIssued,
  markBetaEventCodeRequestRejected,
  updateBetaEventCodeRequestStatus,
} from '../../services/platformBetaEventCodeRequestService';
import {
  getBetaEventCodeAutoSendSettings,
  setBetaEventCodeAutoSendEnabled,
} from '../../services/platformBetaEventCodeSettingsService';
import { sendBetaEventCodeIssueEmail } from '../../services/betaEventCodeIssueEmailService';
import { sendBetaEventCodeRejectEmail } from '../../services/betaEventCodeRejectEmailService';
import { fetchEventPassCodesAdmin } from '../../services/platformPassCodesService';
import {
  isBetaTesterPassCode,
  parseBetaTesterCouponCodeInput,
} from '../../utils/groupPassCoupon';
import type {
  BetaEventCodeRequestRow,
  BetaEventCodeRequestStatus,
} from '../../types/betaEventCodeRequest';

const REJECT_MAIL_FIXED_LINE = '베타 테스트에 참여해주셔서 감사합니다.';
const REJECT_MAIL_PREVIEW_MAX = 36;
const REQUEST_PAGE_SIZE = 20;
const AUTO_SEND_POLL_MS = 30_000;

/** 베타 테스터 코드 목록에서 saleStatus=active 코드 선택(여유 슬롯 우선). */
async function pickActiveBetaTesterCode(): Promise<string | null> {
  const packs = await fetchEventPassCodesAdmin();
  const active = packs.filter(
    (p) => isBetaTesterPassCode(p.code) && p.saleStatus === 'active',
  );
  if (active.length === 0) return null;

  const withSlots = active
    .filter((p) => Number(p.activatedUserCount || 0) < Number(p.activeUserCount || 0))
    .sort((a, b) => {
      const remainA = Number(a.activeUserCount) - Number(a.activatedUserCount);
      const remainB = Number(b.activeUserCount) - Number(b.activatedUserCount);
      if (remainB !== remainA) return remainB - remainA;
      return String(b.issuedAt).localeCompare(String(a.issuedAt));
    });
  if (withSlots.length > 0) return withSlots[0].code;

  return [...active].sort((a, b) => String(b.issuedAt).localeCompare(String(a.issuedAt)))[0]
    ?.code ?? null;
}

function buildRejectMailBody(adminMessage: string): string {
  const body = adminMessage.trim();
  return body ? `${REJECT_MAIL_FIXED_LINE}\n${body}` : REJECT_MAIL_FIXED_LINE;
}

function truncateRejectMailPreview(full: string, max = REJECT_MAIL_PREVIEW_MAX): string {
  const oneLine = full.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= max) return oneLine;
  return `${oneLine.slice(0, max)}…`;
}

function formatRequestAt(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMailFailText(mail: { skipped?: boolean; error: string }): string {
  if (mail.skipped) {
    return `메일 설정(SMTP)을 확인해 주세요. (${mail.error})`;
  }
  if (/HTTP_404|NOT_FOUND/i.test(mail.error)) {
    return '메일 API가 배포되지 않았습니다. 최신 버전을 배포한 뒤 다시 시도해 주세요.';
  }
  return `메일 발송 실패: ${mail.error}`;
}

const STATUS_LABEL: Record<BetaEventCodeRequestStatus, string> = {
  pending: '접수',
  done: '완료',
  rejected: '반려',
};

/** 관리자: 베타 이벤트 코드 요청 목록·발송·반려 */
const AdminBetaEventCodeRequestsPanel: React.FC = () => {
  const [requests, setRequests] = useState<BetaEventCodeRequestRow[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);
  const [codeDrafts, setCodeDrafts] = useState<Record<string, string>>({});
  const [sendBusyId, setSendBusyId] = useState<string | null>(null);
  const [rejectModalRow, setRejectModalRow] = useState<BetaEventCodeRequestRow | null>(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [requestPage, setRequestPage] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<BetaEventCodeRequestRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [autoSend, setAutoSend] = useState(false);
  const [autoSendLoading, setAutoSendLoading] = useState(true);
  const [autoSendBusy, setAutoSendBusy] = useState(false);
  const { toastState, showError, showSuccess } = useAutoStatusToast(4500);
  const autoSendBusyRef = useRef(false);
  const autoProcessingIdsRef = useRef(new Set<string>());
  const autoFailedIdsRef = useRef(new Set<string>());
  const autoNoCodeWarnedRef = useRef(false);

  const reloadRequests = useCallback(async () => {
    try {
      setRequestsLoading(true);
      setRequestsError(null);
      const rows = await listBetaEventCodeRequests();
      setRequests(rows);
      return rows;
    } catch (err) {
      console.error(err);
      setRequestsError(
        '이벤트 코드 요청 목록을 불러오지 못했습니다. Supabase에 reject_message 컬럼 마이그레이션(20260803170000)을 적용했는지 확인해 주세요.',
      );
      return null;
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadRequests();
  }, [reloadRequests]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setAutoSendLoading(true);
        const settings = await getBetaEventCodeAutoSendSettings();
        if (!cancelled) setAutoSend(settings.autoSendEnabled);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          showError(
            '자동발송 설정을 불러오지 못했습니다. 마이그레이션(20260917090000) 적용 여부를 확인해 주세요.',
          );
        }
      } finally {
        if (!cancelled) setAutoSendLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showError]);

  const processAutoSend = useCallback(
    async (rows: BetaEventCodeRequestRow[]) => {
      if (!autoSend || autoSendBusyRef.current) return;
      const pending = rows.filter(
        (r) =>
          r.status === 'pending' &&
          !autoProcessingIdsRef.current.has(r.id) &&
          !autoFailedIdsRef.current.has(r.id),
      );
      if (pending.length === 0) return;

      autoSendBusyRef.current = true;
      try {
        // pending 백로그 처리(관리자 화면이 열려 있을 때). 신규 요청은 submit RPC가 접수 즉시 발급.
        let activeCode: string | null = null;
        try {
          activeCode = await pickActiveBetaTesterCode();
        } catch (err) {
          console.error(err);
          if (!autoNoCodeWarnedRef.current) {
            autoNoCodeWarnedRef.current = true;
            showError('자동발송: 베타 코드 목록을 불러오지 못했습니다.');
          }
          return;
        }
        if (!activeCode) {
          if (!autoNoCodeWarnedRef.current) {
            autoNoCodeWarnedRef.current = true;
            showError('자동발송: 활성화된 베타 테스터 코드가 없습니다.');
          }
          return;
        }
        autoNoCodeWarnedRef.current = false;
        const parsed = parseBetaTesterCouponCodeInput(activeCode);
        if (!parsed) {
          if (!autoNoCodeWarnedRef.current) {
            autoNoCodeWarnedRef.current = true;
            showError('자동발송: 활성 코드 형식이 올바르지 않습니다.');
          }
          return;
        }

        for (const row of pending) {
          autoProcessingIdsRef.current.add(row.id);
          setCodeDrafts((d) => ({ ...d, [row.id]: parsed }));
          setSendBusyId(row.id);
          try {
            const mail = await sendBetaEventCodeIssueEmail({
              to: row.email,
              fullName: row.fullName,
              organization: row.organization,
              eventCode: parsed,
            });
            if (!mail.ok) {
              autoFailedIdsRef.current.add(row.id);
              autoProcessingIdsRef.current.delete(row.id);
              showError(formatMailFailText(mail));
              break;
            }
            try {
              const updated = await markBetaEventCodeRequestIssued(row.id, parsed);
              setRequests((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
              setCodeDrafts((d) => ({ ...d, [row.id]: parsed }));
            } catch (err) {
              console.error(err);
              setRequests((prev) =>
                prev.map((r) =>
                  r.id === row.id ? { ...r, issuedCode: parsed, status: 'done' as const } : r,
                ),
              );
            }
            showSuccess(`${row.fullName}님에게 코드를 자동 발송했습니다.`);
          } catch (err) {
            console.error(err);
            autoFailedIdsRef.current.add(row.id);
            autoProcessingIdsRef.current.delete(row.id);
            showError('자동발송 중 오류가 발생했습니다.');
            break;
          } finally {
            setSendBusyId(null);
          }
        }
      } finally {
        autoSendBusyRef.current = false;
      }
    },
    [autoSend, showError, showSuccess],
  );

  useEffect(() => {
    if (!autoSend || requestsLoading || autoSendLoading) return;
    void processAutoSend(requests);
  }, [autoSend, autoSendLoading, requests, requestsLoading, processAutoSend]);

  useEffect(() => {
    if (!autoSend) return;
    const timer = window.setInterval(() => {
      void reloadRequests();
    }, AUTO_SEND_POLL_MS);
    return () => window.clearInterval(timer);
  }, [autoSend, reloadRequests]);

  const toggleAutoSend = async () => {
    if (autoSendBusy || autoSendLoading) return;
    const next = !autoSend;
    setAutoSendBusy(true);
    try {
      const settings = await setBetaEventCodeAutoSendEnabled(next);
      setAutoSend(settings.autoSendEnabled);
      if (settings.autoSendEnabled) {
        autoFailedIdsRef.current.clear();
        autoNoCodeWarnedRef.current = false;
        showSuccess('자동발송을 켰습니다. 새 요청에 코드가 바로 발송됩니다.');
      } else {
        autoProcessingIdsRef.current.clear();
        autoFailedIdsRef.current.clear();
        autoNoCodeWarnedRef.current = false;
        showSuccess('자동발송을 껐습니다.');
      }
    } catch (err) {
      console.error(err);
      showError(
        '자동발송 설정을 저장하지 못했습니다. 마이그레이션(20260917090000) 적용 여부를 확인해 주세요.',
      );
    } finally {
      setAutoSendBusy(false);
    }
  };

  const requestPageCount = Math.max(1, Math.ceil(requests.length / REQUEST_PAGE_SIZE));
  const safeRequestPage = Math.min(requestPage, requestPageCount - 1);
  const pagedRequests = useMemo(() => {
    const start = safeRequestPage * REQUEST_PAGE_SIZE;
    return requests.slice(start, start + REQUEST_PAGE_SIZE);
  }, [requests, safeRequestPage]);

  useEffect(() => {
    if (requestPage > requestPageCount - 1) {
      setRequestPage(Math.max(0, requestPageCount - 1));
    }
  }, [requestPage, requestPageCount]);

  const handleRequestStatus = async (id: string, status: BetaEventCodeRequestStatus) => {
    if (status === 'rejected') {
      const row = requests.find((r) => r.id === id);
      if (!row) return;
      // 이미 반려 메일을 보낸 적 있으면 모달·재발송 없이 상태만 반려로 변경
      if (row.rejectMessage.trim()) {
        if (statusBusyId || row.status === 'rejected') return;
        try {
          setStatusBusyId(id);
          const updated = await updateBetaEventCodeRequestStatus(id, 'rejected');
          setRequests((rows) => rows.map((r) => (r.id === id ? updated : r)));
        } catch (err) {
          console.error(err);
          setRequestsError('요청 상태 변경에 실패했습니다.');
        } finally {
          setStatusBusyId(null);
        }
        return;
      }
      setRejectModalRow(row);
      setRejectMessage('');
      setRejectError(null);
      return;
    }
    if (statusBusyId) return;
    try {
      setStatusBusyId(id);
      const updated = await updateBetaEventCodeRequestStatus(id, status);
      setRequests((rows) => rows.map((r) => (r.id === id ? updated : r)));
    } catch (err) {
      console.error(err);
      setRequestsError('요청 상태 변경에 실패했습니다.');
    } finally {
      setStatusBusyId(null);
    }
  };

  const closeRejectModal = () => {
    if (rejectBusy) return;
    setRejectModalRow(null);
    setRejectMessage('');
    setRejectError(null);
  };

  const handleSendReject = async () => {
    if (!rejectModalRow || rejectBusy) return;
    const message = rejectMessage.trim();
    if (!message) {
      setRejectError('메일 본문을 입력해 주세요.');
      return;
    }
    try {
      setRejectBusy(true);
      setRejectError(null);
      const mail = await sendBetaEventCodeRejectEmail({
        to: rejectModalRow.email,
        fullName: rejectModalRow.fullName,
        organization: rejectModalRow.organization,
        message,
      });
      if (!mail.ok) {
        setRejectError(formatMailFailText(mail));
        return;
      }
      try {
        const updated = await markBetaEventCodeRequestRejected(rejectModalRow.id, message);
        setRequests((rows) => rows.map((r) => (r.id === rejectModalRow.id ? updated : r)));
      } catch (err) {
        console.error(err);
        setRequests((rows) =>
          rows.map((r) =>
            r.id === rejectModalRow.id
              ? { ...r, status: 'rejected' as const, rejectMessage: message }
              : r,
          ),
        );
      }
      setRejectModalRow(null);
      setRejectMessage('');
    } catch (err) {
      console.error(err);
      setRejectError('반려 메일 발송 중 오류가 발생했습니다.');
    } finally {
      setRejectBusy(false);
    }
  };

  const handleSendEventCode = async (row: BetaEventCodeRequestRow) => {
    if (sendBusyId) return;
    const parsed = parseBetaTesterCouponCodeInput(codeDrafts[row.id] ?? row.issuedCode ?? '');
    if (!parsed) {
      showError('베타 테스터 코드 형식이 올바르지 않습니다. 예: CBT-ABCD2345EFGH');
      return;
    }
    try {
      setSendBusyId(row.id);
      const mail = await sendBetaEventCodeIssueEmail({
        to: row.email,
        fullName: row.fullName,
        organization: row.organization,
        eventCode: parsed,
      });
      if (!mail.ok) {
        showError(formatMailFailText(mail));
        return;
      }
      try {
        const updated = await markBetaEventCodeRequestIssued(row.id, parsed);
        setRequests((rows) => rows.map((r) => (r.id === row.id ? updated : r)));
        setCodeDrafts((d) => ({ ...d, [row.id]: parsed }));
      } catch (err) {
        console.error(err);
        setRequests((rows) =>
          rows.map((r) =>
            r.id === row.id ? { ...r, issuedCode: parsed, status: 'done' as const } : r,
          ),
        );
      }
    } catch (err) {
      console.error(err);
      showError('코드 발송 중 오류가 발생했습니다.');
    } finally {
      setSendBusyId(null);
    }
  };

  const closeDeleteModal = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDeleteRequest = async () => {
    if (!deleteTarget || deleteBusy) return;
    try {
      setDeleteBusy(true);
      setDeleteError(null);
      await deleteBetaEventCodeRequest(deleteTarget.id);
      setRequests((rows) => rows.filter((r) => r.id !== deleteTarget.id));
      setCodeDrafts((d) => {
        const next = { ...d };
        delete next[deleteTarget.id];
        return next;
      });
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      setDeleteError('요청 삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <article
        className="cp-dashboard-block cp-admin-payment-requests cp-admin-users-pass-block"
        aria-labelledby="cp-admin-beta-event-code-requests-head"
      >
        <h3 className="cp-dashboard-block__head" id="cp-admin-beta-event-code-requests-head">
          <span className="cp-admin-beta-head-pager" role="group" aria-label="이벤트 코드 요청 페이지">
            <button
              type="button"
              className="cp-admin-beta-head-pager-btn"
              aria-label="이전 페이지"
              disabled={safeRequestPage <= 0}
              onClick={() => setRequestPage((p) => Math.max(0, p - 1))}
            >
              &lt;
            </button>
            <button
              type="button"
              className="cp-admin-beta-head-pager-btn"
              aria-label="다음 페이지"
              disabled={safeRequestPage >= requestPageCount - 1 || requests.length === 0}
              onClick={() => setRequestPage((p) => Math.min(requestPageCount - 1, p + 1))}
            >
              &gt;
            </button>
          </span>
          <span className="cp-dashboard-block__head-title">베타 이벤트 코드 요청</span>
          <span className="cp-admin-beta-event-auto-send" aria-label="베타 코드 자동발송">
            <span className="cp-admin-beta-event-auto-send-label">자동발송</span>
            <button
              type="button"
              className={'team-auto-approve-toggle' + (autoSend ? ' on' : '')}
              onClick={() => void toggleAutoSend()}
              disabled={autoSendBusy || autoSendLoading}
              aria-pressed={autoSend}
              aria-label="베타 코드 자동발송"
              title={
                autoSendLoading
                  ? '자동발송 설정을 불러오는 중'
                  : autoSend
                    ? '켜짐(DB 공통): 새 요청에 활성 베타 코드를 자동 발급·메일 발송합니다'
                    : '꺼짐(DB 공통): 수동으로 코드를 입력해 발송합니다'
              }
            >
              <span className="team-auto-approve-toggle-slider" data-on={!autoSend}>
                OFF
              </span>
              <span className="team-auto-approve-toggle-slider" data-on={autoSend}>
                ON
              </span>
            </button>
          </span>
        </h3>
        <div className="cp-dashboard-block__body cp-admin-beta-event-requests-body">
          {requestsError ? (
            <p className="cp-dashboard-placeholder" role="alert">
              {requestsError}
            </p>
          ) : requestsLoading ? (
            <p className="cp-dashboard-placeholder" role="status">
              불러오는 중…
            </p>
          ) : requests.length === 0 ? (
            <p className="cp-dashboard-placeholder" role="status">
              접수된 요청이 없습니다.
            </p>
          ) : (
            <div className="cp-admin-payment-requests-table-wrap">
              <table className="cp-admin-payment-requests-table">
                <thead>
                  <tr>
                    <th>접수</th>
                    <th>이름</th>
                    <th>이메일</th>
                    <th>소속</th>
                    <th className="cp-admin-beta-request-memo-col">메모</th>
                    <th className="cp-admin-beta-request-issue-col">코드 발송</th>
                    <th className="cp-admin-beta-request-status-col">상태</th>
                    <th className="cp-admin-beta-request-delete-col">삭제</th>
                  </tr>
                </thead>
                <tbody>
                {pagedRequests.map((r) => {
                  const draft = codeDrafts[r.id] ?? r.issuedCode ?? '';
                  const rejectMailFull =
                    r.status === 'rejected' ? buildRejectMailBody(r.rejectMessage) : '';
                  const rejectMailPreview =
                    r.status === 'rejected' ? truncateRejectMailPreview(rejectMailFull) : '';
                  return (
                    <tr key={r.id}>
                      <td>{formatRequestAt(r.createdAt)}</td>
                      <td>{r.fullName}</td>
                      <td>{r.email}</td>
                      <td>{r.organization || '—'}</td>
                      <td className="cp-admin-beta-request-memo-col">{r.note || '—'}</td>
                      <td className="cp-admin-beta-request-issue-col">
                        <div className="cp-admin-payment-request-issue">
                          {r.status === 'done' ? (
                            <div className="cp-admin-payment-request-issue-row">
                              <span className="cp-admin-payment-request-code-static">
                                안내한 코드 : {r.issuedCode || '—'}
                              </span>
                              <span
                                className="cp-admin-payment-request-send-btn is-status is-sent"
                                role="status"
                              >
                                발송완료
                              </span>
                            </div>
                          ) : r.status === 'rejected' ? (
                            <div className="cp-admin-payment-request-issue-row">
                              <span className="cp-admin-payment-request-reject-preview-wrap">
                                <span className="cp-admin-payment-request-code-static cp-admin-payment-request-reject-preview">
                                  {rejectMailPreview}
                                </span>
                                <span
                                  className="cp-admin-payment-request-reject-popup"
                                  role="tooltip"
                                >
                                  {rejectMailFull}
                                </span>
                              </span>
                              <span
                                className="cp-admin-payment-request-send-btn is-status is-unsent"
                                role="status"
                              >
                                요청반려
                              </span>
                            </div>
                          ) : (
                            <div className="cp-admin-payment-request-issue-row">
                              <input
                                type="text"
                                className="cp-admin-payment-request-code-input"
                                placeholder="CBT-…"
                                autoComplete="off"
                                spellCheck={false}
                                value={draft}
                                disabled={sendBusyId === r.id}
                                onChange={(e) =>
                                  setCodeDrafts((d) => ({ ...d, [r.id]: e.target.value }))
                                }
                              />
                              <button
                                type="button"
                                className="cp-admin-payment-request-send-btn"
                                disabled={sendBusyId === r.id || !draft.trim()}
                                onClick={() => void handleSendEventCode(r)}
                              >
                                {sendBusyId === r.id ? '발송중…' : '보내기'}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="cp-admin-beta-request-status-col">
                        <div
                          className="cp-admin-payment-request-status"
                          role="group"
                          aria-label="요청 상태 변경"
                        >
                          {(['pending', 'done', 'rejected'] as const).map((s) => (
                            <button
                              key={s}
                              type="button"
                              className={`cp-admin-payment-request-status-btn${r.status === s ? ' is-active' : ''}`}
                              disabled={statusBusyId === r.id}
                              title={`${STATUS_LABEL[s]}(으)로 상태 변경`}
                              onClick={() => void handleRequestStatus(r.id, s)}
                            >
                              {STATUS_LABEL[s]}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="cp-admin-beta-request-delete-col">
                        <button
                          type="button"
                          className="cp-admin-notices-icon-btn"
                          title="요청 삭제"
                          aria-label={`${r.fullName} 코드 요청 삭제`}
                          disabled={deleteBusy}
                          onClick={() => {
                            setDeleteError(null);
                            setDeleteTarget(r);
                          }}
                        >
                          <img
                            src="/Trash.svg"
                            alt=""
                            className="cp-admin-notices-tool-icon"
                            width={15}
                            height={15}
                            decoding="async"
                          />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </article>

      {rejectModalRow ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={closeRejectModal}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeRejectModal();
          }}
        >
          <div
            className="cp-modal cp-admin-payment-reject-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-payment-reject-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-payment-reject-title">베타 코드 요청 반려</h3>
            <div className="cp-modal-body">
              <p className="cp-admin-payment-reject-meta">
                {rejectModalRow.fullName} · {rejectModalRow.email}
                {rejectModalRow.organization ? ` · ${rejectModalRow.organization}` : ''}
              </p>
              <label className="cp-admin-payment-reject-label" htmlFor="cp-admin-payment-reject-msg">
                메일 본문
              </label>
              <div
                className={`cp-admin-payment-reject-compose${rejectBusy ? ' is-disabled' : ''}`}
              >
                <p className="cp-admin-payment-reject-compose-fixed" aria-hidden="true">
                  {REJECT_MAIL_FIXED_LINE}
                </p>
                <textarea
                  id="cp-admin-payment-reject-msg"
                  className="cp-admin-payment-reject-textarea"
                  rows={6}
                  maxLength={4000}
                  placeholder="이어서 전달할 내용을 입력해 주세요."
                  value={rejectMessage}
                  disabled={rejectBusy}
                  aria-describedby="cp-admin-payment-reject-fixed-line"
                  onChange={(e) => setRejectMessage(e.target.value)}
                />
                <span id="cp-admin-payment-reject-fixed-line" className="cp-visually-hidden">
                  첫 줄은 고정 문구입니다: {REJECT_MAIL_FIXED_LINE}
                </span>
              </div>
              {rejectError ? (
                <p className="cp-admin-payment-request-issue-msg is-error" role="alert">
                  {rejectError}
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={closeRejectModal}
                disabled={rejectBusy}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleSendReject()}
                disabled={rejectBusy || !rejectMessage.trim()}
              >
                {rejectBusy ? '발송 중…' : '보내기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={closeDeleteModal}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeDeleteModal();
          }}
        >
          <div
            className="cp-modal cp-admin-beta-delete-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-beta-request-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-beta-request-delete-title">코드 요청 삭제</h3>
            <div className="cp-modal-body">
              <p>이 이벤트 코드 요청을 삭제할까요?</p>
              <p className="cp-admin-beta-delete-confirm-meta">
                {deleteTarget.fullName} · {deleteTarget.email}
                {deleteTarget.organization ? ` · ${deleteTarget.organization}` : ''}
              </p>
              {deleteError ? (
                <p className="cp-dashboard-placeholder" role="alert">
                  {deleteError}
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={closeDeleteModal}
                disabled={deleteBusy}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void confirmDeleteRequest()}
                disabled={deleteBusy}
              >
                {deleteBusy ? '삭제 중…' : '삭제하기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <AutoStatusToast state={toastState} />
    </>
  );
};

export default AdminBetaEventCodeRequestsPanel;
